import {Inject, Injectable, LOCALE_ID} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {KeycloakService} from 'keycloak-angular';
import {NGXLogger} from 'ngx-logger';
import {filter, firstValueFrom} from 'rxjs';
import {SecurityConfigService, UserInfo} from '@twpub/shared/services/security-config.service';
import {getDecodedJwt} from '../utils/jwt-util';
import {environment} from '../../../environments/environment';

type RequiredGroupKey = 'search' | 'admin' | 'super_admin';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private localePrefix: string;

  constructor(private logger: NGXLogger, private router: Router,
              private keycloak: KeycloakService,
              private securityService: SecurityConfigService,
              @Inject(LOCALE_ID) localeId: string
  ) {
    this.localePrefix = environment.production ? `/${localeId.split('-')[0]}` : '';
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.isAccessAllowed(route, state);
  }

  async isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    // Wait for auth initialization to complete before checking permissions.
    // This prevents race conditions with initialNavigation: 'enabledNonBlocking'
    // where the guard runs before APP_INITIALIZER finishes loading security config.
    await firstValueFrom(this.securityService.authInitialized$.pipe(filter(v => v)));

    const cfg = this.securityService.getConfig();
    const requiredKeys: RequiredGroupKey[] = (route.data?.requiredGroups as RequiredGroupKey[] | undefined) ?? ['search'];
    const isSearchOnly = requiredKeys.includes('search') && !requiredKeys.includes('admin');
    if (cfg.anonymousEnabled && isSearchOnly) {
      return true;
    }

    const isKeycloakAdmin = this.securityService.isKeycloakAdminMode();
    const isExternal = this.securityService.isExternalAuth();

    // If user is in Keycloak admin mode, use Keycloak auth flow even if external auth is configured
    if (isKeycloakAdmin) {
      this.logger.debug('AuthGuard: Using Keycloak auth flow (keycloakAdminMode)');
      return this.handleKeycloakAuth(route, state);
    }

    if (isExternal) {
      this.logger.debug('AuthGuard: Using external auth flow');
      return this.handleExternalAuth(route);
    }
    return this.handleKeycloakAuth(route, state);
  }

  private async handleExternalAuth(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const isAuthenticated = this.securityService.isUserAuthenticated();
    const user = this.securityService.getUser();
    if (isAuthenticated && user) {
      return this.authenticateRoute(route, user);
    }
    await this.securityService.login();
    return false;
  }

  private authenticateRoute = (route: ActivatedRouteSnapshot, user: UserInfo) => {
    const cfg = this.securityService.getConfig();
    const adminRequired = this.isAdminRequired(route);
    const superAdminRequired = this.isSuperAdminRequired(route);

    if (this.securityService.isKeycloakAdminMode()) {
      return true;
    }

    // If user was authenticated via ABAC rules, skip group validation
    // ABAC already verified the user has access based on their attributes
    if (user.abacAuthenticated) {
      this.logger.debug('AuthGuard.authenticateRoute: User authenticated via ABAC, skipping group validation');
      return true;
    }

    const userGroups = user.groups || [];
    // Normalize group names: remove leading slash for comparison
    const normalizeGroup = (g: string) => g?.startsWith('/') ? g.slice(1) : g;
    const normalizedUserGroups = userGroups.map(normalizeGroup);
    const hasGroup = (group: string) => normalizedUserGroups.includes(normalizeGroup(group));

    // If no admin groups are configured, admin access is not possible via groups
    const adminGroups = cfg.adminUserGroups ?? [];
    const isAdmin = adminGroups.length > 0 ? adminGroups.some(g => hasGroup(g)) : false;

    // Check super admin access
    if (superAdminRequired) {
      const isSuperAdmin = this.securityService.isSuperAdmin();
      if (!isSuperAdmin) {
        return this.router.parseUrl('/access-denied');
      }
      return true;
    }

    // If no groups are configured from SSO, allow access for authenticated users
    const noGroupsConfigured = !cfg.searchUserGroup && adminGroups.length === 0;

    if (noGroupsConfigured) {
      this.logger.debug('AuthGuard.handleExternalAuth: No groups configured, allowing access for authenticated user');
      return true;
    }

    // Original logic for when groups are configured
    const authenticated = isAdmin || (!adminRequired && (!cfg.searchUserGroup || hasGroup(cfg.searchUserGroup)));

    if (!authenticated) {
      // Redirect to access-denied page instead of logging out to prevent loop
      return this.router.parseUrl('/access-denied');
    }
    return authenticated;
  }

  private isAdminRequired = (route: ActivatedRouteSnapshot) => {
    const groups = (route.data?.requiredGroups as RequiredGroupKey[] | undefined) ?? ['search'];
    return groups.includes('admin') || groups.includes('super_admin');
  }

  private isSuperAdminRequired = (route: ActivatedRouteSnapshot) => {
    return ((route.data?.requiredGroups as RequiredGroupKey[] | undefined) ?? ['search']).includes('super_admin');
  }

  private async handleKeycloakAuth(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const isAuthenticatedViaSecurityService = this.securityService.isUserAuthenticated();
    const user = this.securityService.getUser();
    if (isAuthenticatedViaSecurityService && user) {
      return this.authenticateRoute(route, user);
    }

    // Ensure Keycloak is available
    if (!this.keycloak) {
      this.logger.error('Keycloak service not available');
      return this.router.parseUrl('/');
    }

    // Ensure Keycloak is initialized (may have been skipped during anonymous startup)
    try {
      await this.keycloak.isLoggedIn();
    } catch {
      this.logger.debug('AuthGuard: Keycloak not initialized, initializing now');
      await firstValueFrom(this.securityService.initializeKeycloak());
    }

    try {
      if (!await this.keycloak.isLoggedIn()) {
        await this.loginKeycloak(state);
        return false;
      }

      const cfg = this.securityService.getConfig();
      const requiredKeys: RequiredGroupKey[] = (route.data?.requiredGroups as RequiredGroupKey[] | undefined) ?? ['search'];
      const adminRequired = requiredKeys.includes('admin') || requiredKeys.includes('super_admin');
      const superAdminRequired = requiredKeys.includes('super_admin');

      if (this.securityService.isKeycloakAdminMode()) {
        return true;
      }

      // Check super admin access early
      if (superAdminRequired) {
        const isSuperAdmin = this.securityService.isSuperAdmin();
        if (!isSuperAdmin) {
          return this.router.parseUrl('/access-denied');
        }
        return true;
      }

      // If no groups are configured from SSO, allow access for authenticated users
      const adminGroups = cfg.adminUserGroups ?? [];
      const noGroupsConfigured = !cfg.searchUserGroup && adminGroups.length === 0;

      if (noGroupsConfigured) {
        this.logger.debug('AuthGuard.handleKeycloakAuth: No groups configured, allowing access for authenticated user');
        return true;
      }

      const tokenParsed: any = getDecodedJwt();
      const groups: string[] = this.parseGroups(tokenParsed);
      const authorities: string[] = this.parseAuthorities(tokenParsed);
      // Normalize group names: remove leading slash for comparison
      const normalizeGroup = (g: string) => g?.startsWith('/') ? g.slice(1) : g;
      const hasAuth = (group: string) => {
        const normalizedGroup = normalizeGroup(group);
        return groups.includes(normalizedGroup) || authorities.includes(normalizedGroup);
      };

      // If no admin groups are configured, admin access is not possible via groups
      const isAdmin = adminGroups.length > 0 ? adminGroups.some(g => hasAuth(g)) : false;

      // Original logic for when groups are configured
      const authenticated = isAdmin || (!adminRequired && (!cfg.searchUserGroup || hasAuth(cfg.searchUserGroup)));

      if (!authenticated) {
        this.logger.warn('AuthGuard.handleKeycloakAuth: User does not have required groups', {
          groups,
          authorities,
          requiredSearchGroup: cfg.searchUserGroup,
          requiredAdminGroups: cfg.adminUserGroups,
          adminRequired,
          isAdmin
        });
        // Redirect to access-denied page instead of logging out to prevent loop
        return this.router.parseUrl('/access-denied');
      }

      return authenticated;
    } catch (error) {
      this.logger.error('Error during Keycloak authentication:', error);
      await this.loginKeycloak(state);
      return false;
    }
  }

  private loginKeycloak = async (state: RouterStateSnapshot) => {
    if (!this.keycloak) {
      this.logger.error('Keycloak service not available for login');
      return;
    }
    const redirectUri = window.location.origin + this.localePrefix + state.url;
    try {
      await this.keycloak.login({redirectUri});
    } catch {
      // Keycloak adapter not initialized — re-initialize with login-required to trigger redirect
      this.logger.debug('AuthGuard: Keycloak login failed, reinitializing with login-required');
      await this.securityService.initializeKeycloakWithLogin(redirectUri);
    }
  }

  private parseAuthorities = (tokenParsed: any) => {
    return (tokenParsed?.authorities as string[] ?? []).map(a => {
      if (a.startsWith('GROUP_')) return a.slice(6);
      if (a.startsWith('/')) return a.slice(1);
      return a;
    });
  }

  private parseGroups = (tokenParsed: any) => {
    return (tokenParsed?.groups as string[] ?? []).map(g => g?.startsWith('/') ? g.slice(1) : g);
  }
}
